import romanApi, { type RomanApiError } from "@roman/shared/lib/net/romanApi";
import type { Language, LanguageQuery } from "@roman/shared/types/Language";
import useSWR from "swr";

type GameStat = {
  goalsScored: number;
  assists: number;
  shotsOffTarget: number;
  shotsOnTarget: number;
  crossesSuccessful: number;
  chancesCreated: number;
  passesSuccessful: number;
  foulsCommitted: number;
  wasFouled: number;
  tacklesSuccessful: number;
  interceptions: number;
  yellowCards: number;
  redCards: number;
  shotsFacedSaved: number;
  penaltiesSaved: number;
  goalsConceded: number;
  cleanSheets: number;
  wonGame: number;
};

export type PlayerStats =
  | {
      played: true;
      startDate: Date;
      opponent: string;
      matchScore: string;
      fantasyPoint: number;
      gameStat: GameStat;
    }
  | {
      played: false;
      startDate: Date;
      opponent: string;
      matchScore: string;
    };

interface RecentPlayerStatsResponse {
  name: string;
  nationality: string;
  dateOfBirth: number | null;
  height: number | null;
  weight: number | null;
  seasonName: string;
  recentStats: PlayerStats[];
}

interface UseRecentPlayerStatsReturn {
  data?: RecentPlayerStatsResponse;
  error: any;
  isLoading: boolean;
}

const fetcher = async (url: string, lang: Language) => {
  const response = await romanApi.get<RecentPlayerStatsResponse, LanguageQuery>(url, { lang });

  const transformedData: RecentPlayerStatsResponse = {
    name: response.name,
    nationality: response.nationality,
    dateOfBirth: response.dateOfBirth,
    height: response.height,
    weight: response.weight,
    seasonName: response.seasonName,
    recentStats: response.recentStats.map((fp) => ({
      ...fp,
      startDate: new Date(fp.startDate),
    })),
  };

  return transformedData;
};

export const useRecentPlayerStats = (
  playerId: string,
  roundId: string,
  handleError: (err: RomanApiError) => void,
  lang: Language = "en",
): UseRecentPlayerStatsReturn => {
  const { data, error, isLoading } = useSWR<RecentPlayerStatsResponse>(
    `/soccer/player/statistics/${playerId}/${roundId}`,
    (url: string) => fetcher(url, lang),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        handleError(error);
      },
    },
  );

  return {
    data,
    error,
    isLoading,
  };
};
